'use client';

import { ApolloProvider as ApolloProviderBase } from '@apollo/client/react';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  useCurrentUser,
  classifyAuthRejection,
  markSessionRejected,
} from '@paeffgen/react/crm-documents/feature-auth';
import { createApolloClient } from './apollo-client';

/**
 * Apollo Client provider for the CRM Documents Client.
 *
 * - Creates a single ApolloClient instance that persists across renders.
 * - Injects the current access token into every GraphQL request via a
 *   dynamic auth link (token changes don't recreate the client or clear cache).
 * - Must be wrapped by SessionProvider (for useCurrentUser to work).
 */
export function GraphQLProvider({ children }: { children: ReactNode }) {
  const { accessToken, isLoading } = useCurrentUser();
  const tokenRef = useRef<string | undefined>(undefined);

  // Sync token into the ref on EVERY render (synchronously, before queries fire).
  // useEffect would be too late — queries start before the effect runs.
  tokenRef.current = accessToken;

  const client = useMemo(
    () =>
      createApolloClient(
        () => tokenRef.current,
        // Lehnt die API unsere Anmeldung ab, wird das im UI zum Hinweis mit
        // „Sitzung erneuern" (SessionRenewalNotice). Nötig, weil eine
        // serverseitig beendete Keycloak-Session der Session lokal nicht
        // anzusehen ist: das Token bleibt formal tagelang gültig, es wird
        // also auch kein Refresh ausgelöst. Nur die API weiß es.
        (messages, hadToken) => {
          const reason = classifyAuthRejection(messages, hadToken);
          if (reason) markSessionRejected(reason);
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Don't render until session is loaded — prevents unauthenticated queries.
  if (isLoading) {
    return null;
  }

  return (
    <ApolloProviderBase client={client}>{children}</ApolloProviderBase>
  );
}
